import { create } from 'zustand';
import { LoginCredentials } from 'types/index';
import {User} from 'types/user';
import { authService } from 'services/auth.service';
import { tokenStorage } from 'utils/storage';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (credentials: LoginCredentials) => Promise<any>;
  forgotPassword: (email: string) => Promise<any>;
  changePassword: (id: string,password: string) => Promise<any>;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
  clearError: () => void;
  initializeAuth: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,

  login: async (credentials: LoginCredentials) => {
    set({ isLoading: true, error: null });
    try {
      const { user } = await authService.login(credentials);
      const accessToken = tokenStorage.getAccessToken();
      const hasValidToken = !!(accessToken && accessToken.trim() !== "");

      set({
        user,
        isAuthenticated: hasValidToken,
        isLoading: false,
        error: null,
      });
      return user;
    } catch (error: any) {
      set({
        error: error.message || 'Login failed',
        isLoading: false,
        isAuthenticated: false,
      });
      throw error;
    }
  },

  forgotPassword: async (email: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.forgotPassword(email);
      return response;
    } catch (error: any) {
      set({
        error: error.message || 'Failed to send password reset link',
        isLoading: false,
      });
      throw error;
    }finally{
      set({ isLoading: false });
    }
  },

  changePassword: async (id: string,password: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await authService.changePassword(id,password);
      return response;
    } catch (error: any) {
      set({
        error: error.message || 'Failed to send password reset link',
        isLoading: false,
      });
      throw error;
    }finally{
      set({ isLoading: false });
    }
  },

  logout: async () => {
    set({ isLoading: true });
    try {
      await authService.logout();
    } finally {
      try {
        sessionStorage.setItem('__manual_logout__', 'true');
      } catch {}
      authService.clearSession();
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  },

  updateUser: (user: User) => {
    set({ user });
  },

  clearError: () => {
    set({ error: null });
  },

  initializeAuth: () => {
    set({ isLoading: true });
    const isAuthenticated = authService.isAuthenticated();
    const user = authService.getCurrentUser();
    set({
      user,
      isAuthenticated,
      isLoading: false,
    });
  },
}));

